import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Prompt } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import BackflowIssueReport from '../components/BackflowIssueReport';
import BackflowJobItemInfoHeader from '../components/BackflowJobItemInfoHeader';
import BackflowLogSection from '../components/BackflowLogSection';
import DeviceAccountNumberSection from '../components/DeviceAccountNumberSection';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import JobItemHistory from '../components/JobItemHistory';
import JobItemSection from '../components/JobItemSection';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import StatusCell from '../../../sharedModules/layout/StatusCell';

import axios from '../../../sharedModules/utils/axios.utils';

import { BackflowJobItem } from '../../model';
import { findJobItemInCache } from '../../utils/backflowCache';

const BackflowLogPage: React.FC = () => {
  const { id, formAction } = useParams<{ id: string; formAction: string }>();
  const location = useLocation();

  let [backflowJobItem, setBackflowJobItem] = useState<BackflowJobItem | null>(null);
  let [errorMessage, setErrorMessage] = useState<string>('');
  let [loading, setLoading] = useState<boolean>(true);
  let [loadedFromCache, setLoadedFromCache] = useState<boolean>(false);
  let [savedOffline, setSavedOffline] = useState<boolean>(false);

  // Get backLink and offlineOnly from URL params
  const searchParams = new URLSearchParams(location.search);
  const backLinkParam = searchParams.get('backLink');
  const offlineOnly = searchParams.get('offlineOnly') === 'true';

  const fetchBackflowJobItem = async () => {
    setLoading(true);

    try {
      const jobResponse = await axios.get<string, any>(`/backflows/backflow_job_items/${id}`);
      const jobItemData = jobResponse.data.result;
      setBackflowJobItem(jobItemData);
      setErrorMessage(null);
      setLoadedFromCache(false);
    } catch (error: any) {
      // Determine if this is a network error or server error
      const isNetworkError = !navigator.onLine || error.code === 'ERR_NETWORK' || error.message === 'Network Error';

      if (isNetworkError) {
        const jobItem = findJobItemInCache(id);

        if (jobItem) {
          setBackflowJobItem(jobItem);
          setLoadedFromCache(true);
          setErrorMessage(null);
        } else {
          setErrorMessage('No internet connection and no cached data available for this item.');
        }
      } else {
        // Server error
        const statusCode = error.response?.status;

        if (statusCode === 404) {
          setErrorMessage('Backflow job item not found.');
        } else if (statusCode === 403) {
          setErrorMessage('You do not have permission to view this item.');
        } else {
          setErrorMessage('Failed to fetch backflow job item. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      if (offlineOnly) {
        // Only load from cache, don't fetch from server
        const jobItem = findJobItemInCache(id);
        if (jobItem) {
          setBackflowJobItem(jobItem);
          setLoadedFromCache(true);
        } else {
          setErrorMessage('No cached data available for this job item.');
        }
        setLoading(false);
      } else {
        // Normal behavior: fetch from server (with cache fallback on error)
        fetchBackflowJobItem();
      }
    }
  }, [id, offlineOnly]);

  let startedStatuses = ['in_progress', 'approved', 'pending_approval', 'out_of_service'];
  let jobStarted = startedStatuses.includes(backflowJobItem?.status);

  // Warn on browser close/refresh when test has started
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if ((jobStarted && !backflowJobItem?.completed_at && !savedOffline) || loadedFromCache) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [jobStarted, backflowJobItem?.completed_at, savedOffline, loadedFromCache]);

  // Determine back button link and label
  const getBackButtonConfig = () => {
    if (backLinkParam === '/backflow_tests/approvals') {
      return {
        label: 'Approvals Queue',
        link: '/backflow_tests/approvals',
      };
    }
    if (backLinkParam === '/backflow_tests/flagged_devices') {
      return {
        label: 'Flagged Devices',
        link: '/backflow_tests/flagged_devices',
      };
    }
    // Default back to job group
    return {
      label: 'All Backflow Assets',
      link: `/backflow_tests/backflow_job_groups/${backflowJobItem?.backflow_job_group_id}${
        formAction ? `/${formAction}` : ''
      }`,
    };
  };

  const backButtonConfig = getBackButtonConfig();
  const showDeviceAcctNoField = formAction === 'approve' && backflowJobItem?.requires_backflow_device_account_number;

  return (
    <LoadingWrapper isLoading={loading}>
      <Prompt
        when={jobStarted && !backflowJobItem?.completed_at && !savedOffline}
        message="You have a test in progress. Are you sure you want to leave? Any unsaved changes will be lost."
      />
      <Container maxWidth="md">
        <Grid container>
          <Grid item xs={12}>
            <PageHeader
              titleIcon={<PlumbingIcon />}
              mainTitle="Backflow Testing"
              subTitle={`${backflowJobItem?.asset_no}`}
              backButtonLabel={backButtonConfig.label}
              backButtonLink={backButtonConfig.link}
            />
          </Grid>
          {loadedFromCache && (
            <Grid item xs={12} sx={{ mb: 2 }}>
              <Alert severity="info">
                {offlineOnly
                  ? 'Viewing data saved to your device from when you were working offline.'
                  : 'You are currently offline. This data was loaded from your device.  Do not refresh or reload the screen.'}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>
          <Grid item xs={12}>
            <BackflowJobItemInfoHeader backflowJobItem={backflowJobItem} />
          </Grid>
          <Grid item xs={12} style={{ padding: '1% 2%' }}>
            <Typography variant="subtitle1" component="h6" style={{ fontWeight: 600, fontSize: 16 }}>
              Status
            </Typography>
            <StatusCell status={backflowJobItem?.status} />
          </Grid>
          {showDeviceAcctNoField && (
            <Grid item xs={12} style={{ marginTop: 10 }}>
              <DeviceAccountNumberSection
                backflowJobItem={backflowJobItem}
                fetchBackflowJobItem={fetchBackflowJobItem}
              />
            </Grid>
          )}
          <Grid item xs={12} style={{ marginTop: 10 }}>
            <JobItemSection
              backflowJobItem={backflowJobItem}
              fetchBackflowJobItem={fetchBackflowJobItem}
              jobStarted={jobStarted}
            />
          </Grid>
          {formAction === 'approve' && (
            <Grid item xs={12} style={{ marginTop: 10 }}>
              <JobItemHistory backflowJobItem={backflowJobItem} />
            </Grid>
          )}

          <Grid item xs={12} style={{ marginTop: 10 }}>
            <BackflowLogSection
              backflowJobItem={backflowJobItem}
              jobStarted={jobStarted}
              fetchBackflowJobItem={fetchBackflowJobItem}
              onSavedOffline={() => setSavedOffline(true)}
            />
          </Grid>
          <Grid item xs={12} style={{ marginTop: 40 }}>
            <BackflowIssueReport backflowJobItem={backflowJobItem} />
          </Grid>
        </Grid>
      </Container>
    </LoadingWrapper>
  );
};

export default BackflowLogPage;
